import { useEffect, useState } from 'react'

import styles from './CookieConsent.module.scss'

const COOKIE_CONSENT_KEY = 'doorhan_cookie_consent'
const COOKIE_CONSENT_MAX_AGE = 60 * 60 * 24 * 365
const METRIKA_COUNTER_ID = 107075503

type CookieConsentValue = 'necessary' | 'all'

const getCookieConsent = (): CookieConsentValue | null => {
  const cookie = document.cookie
    .split('; ')
    .find(item => item.startsWith(`${COOKIE_CONSENT_KEY}=`))

  const value = cookie?.split('=')[1]

  return value === 'necessary' || value === 'all' ? value : null
}

const saveCookieConsent = (value: CookieConsentValue) => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''

  document.cookie = `${COOKIE_CONSENT_KEY}=${value}; Max-Age=${COOKIE_CONSENT_MAX_AGE}; Path=/; SameSite=Lax${secure}`
}

const enableYandexMetrika = () => {
  if (window.ym) return

  const metrika = ((...args: unknown[]) => {
    metrika.a = metrika.a || []
    metrika.a.push(args)
  }) as NonNullable<Window['ym']>

  metrika.l = Date.now()
  window.ym = metrika

  const script = document.createElement('script')
  script.id = 'yandex-metrika-script'
  script.async = true
  script.src = `https://mc.yandex.ru/metrika/tag.js?id=${METRIKA_COUNTER_ID}`
  document.head.appendChild(script)

  metrika(METRIKA_COUNTER_ID, 'init', {
    ssr: true,
    webvisor: true,
    clickmap: true,
    ecommerce: 'dataLayer',
    referrer: document.referrer,
    url: window.location.href,
    accurateTrackBounce: true,
    trackLinks: true,
  })
}

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const consent = getCookieConsent()

    if (consent === 'all') enableYandexMetrika()
    if (!consent) setIsVisible(true)
  }, [])

  const handleConsent = (value: CookieConsentValue) => {
    saveCookieConsent(value)

    if (value === 'all') enableYandexMetrika()

    setIsVisible(false)
  }

  if (!isVisible) return null

  return (
    <section
      className={styles.banner}
      role='dialog'
      aria-label='Настройки cookie'
      aria-live='polite'
    >
      <div className={styles.content}>
        <p className={styles.text}>
          Этот веб-сайт использует файлы cookie, чтобы Вы могли максимально
          эффективно им пользоваться.
          <a
            href='/privacy-policy'
            target='_blank'
            rel='noreferrer'
            className={styles.policyLink}
          >
            Политика конфиденциальности
          </a>
        </p>

        <div className={styles.actions}>
          <button
            type='button'
            className={styles.necessaryButton}
            onClick={() => handleConsent('necessary')}
          >
            Разрешить только необходимые cookie
          </button>
          <button
            type='button'
            className={styles.allButton}
            onClick={() => handleConsent('all')}
          >
            Разрешить все cookie
          </button>
        </div>
      </div>
    </section>
  )
}
